import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "@core/auth.service";
import { MyToastbarComponent } from "../../app/components/my-toastbar/my-toastbar.component";

export type ToastSeverity = "success" | "info" | "warning" | "error" | "action_required";

export interface ToastPayload {
    code?: string;
    message: { line_1: string; line_2?: string };
    style: {
        type: ToastSeverity;
        icon: string;
        duration_ms: number;
    };
    context?: {
        journey?: string;
        trigger?: string;
        reason_code?: string;
    };
}

@Injectable({ providedIn: "root" })
export class ToastFacadeService {
    private lastToastKey = "";
    private lastToastAt = 0;

    constructor(
        private snackBar: MatSnackBar,
        private authService: AuthService,
    ) { }

    async showByCode(
        code: string,
        fallback: ToastPayload,
        context?: ToastPayload["context"]
    ): Promise<void> {
        const translated = await this.authService.getConstantsMsg(code);
        const payload: ToastPayload = {
            code,
            message: translated?.message ?? fallback.message,
            style: {
                type: this.normalizeType(translated?.style?.type ?? fallback.style.type),
                icon: translated?.style?.icon ?? fallback.style.icon,
                duration_ms: translated?.style?.duration_ms ?? translated?.style?.duration ?? fallback.style.duration_ms,
            },
            context,
        };
        if (this.isDuplicate(payload)) return;
        this.snackBar.openFromComponent(MyToastbarComponent, {
            duration: payload.style.duration_ms,
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: "toast-for-syndicate",
            data: payload,
        });
    }

    showInline(payload: ToastPayload): void {
        if (this.isDuplicate(payload)) return;

        this.snackBar.openFromComponent(MyToastbarComponent, {
            duration: payload.style.duration_ms,
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: "toast-for-syndicate",
            data: payload,
        });
    }

    private normalizeType(type: string): ToastSeverity {
        const t = (type || "").toLowerCase();
        if (t === "success") return "success";
        if (t === "info") return "info";
        if (t === "warning") return "warning";
        if (t === "action_required") return "action_required";
        return "error";
    }

    private isDuplicate(payload: ToastPayload): boolean {
        const key = `${payload.code}|${payload.message?.line_1}|${payload.style?.type}`;
        const now = Date.now();
        if (this.lastToastKey === key && now - this.lastToastAt < 1500) return true;
        this.lastToastKey = key;
        this.lastToastAt = now;
        return false;
    }
}
